import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import html2canvas from 'html2canvas';

interface CheckoutTransitionProps {
  onComplete: () => void;
  // Origin coords are still accepted for API compat with the previous burst
  // implementation but ignored — the disintegration radiates from the entire
  // viewport based on actual UI pixel positions.
  originX?: number;
  originY?: number;
}

const TOTAL_MS = 1300;

interface Particle {
  ox: number;
  oy: number;
  r: number;
  g: number;
  b: number;
  vx: number;
  vy: number;
  delay: number;
  life: number;
  size: number;
}

export function CheckoutTransition({ onComplete }: CheckoutTransitionProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [phase, setPhase] = useState<'capturing' | 'animating'>('capturing');

  useEffect(() => {
    let mounted = true;
    let rafId: number | null = null;
    const completionTimer = setTimeout(onComplete, TOTAL_MS);

    (async () => {
      let sourceCanvas: HTMLCanvasElement;
      try {
        // Capture the visible viewport at low res for speed (~80–200 ms).
        // ignoreElements skips our own overlay so we don't capture the dark
        // backdrop on top of the UI.
        sourceCanvas = await html2canvas(document.body, {
          scale: 0.45,
          backgroundColor: null,
          useCORS: true,
          logging: false,
          x: window.scrollX,
          y: window.scrollY,
          width: window.innerWidth,
          height: window.innerHeight,
          windowWidth: window.innerWidth,
          windowHeight: window.innerHeight,
          ignoreElements: (el) => el.classList?.contains('checkout-transition-root') ?? false,
        });
      } catch {
        // If capture fails for any reason (CORS, weird CSS, etc.) we still
        // need the navigation to fire — the timeout will handle it.
        return;
      }
      if (!mounted) return;

      const sCtx = sourceCanvas.getContext('2d');
      if (!sCtx) return;
      const imageData = sCtx.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
      const w = sourceCanvas.width;
      const h = sourceCanvas.height;
      const captureScale = 0.45;

      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      const maxDist = Math.hypot(cx, cy);

      // Sample every Nth pixel of the captured canvas. SAMPLE = 2 yields
      // about 12k particles after filtering on a 1440×900 viewport — heavy
      // but smooth on canvas because we render via fillRect, not DOM nodes.
      const SAMPLE = 2;
      const particles: Particle[] = [];
      for (let y = 0; y < h; y += SAMPLE) {
        for (let x = 0; x < w; x += SAMPLE) {
          const i = (y * w + x) * 4;
          const r = imageData.data[i];
          const g = imageData.data[i + 1];
          const b = imageData.data[i + 2];
          const a = imageData.data[i + 3];

          if (a < 100) continue;
          // Skip the cosmic/black background — we want only UI pixels to
          // disintegrate, otherwise we sample the whole black void and the
          // effect just looks like a noise field.
          if (r + g + b < 100) continue;

          const screenX = x / captureScale;
          const screenY = y / captureScale;

          const dx = screenX - cx;
          const dy = screenY - cy;
          const distFromCenter = Math.hypot(dx, dy);
          const distNorm = distFromCenter / maxDist;

          // Wave delay: dissolution starts at the bottom-left and sweeps up
          // and to the right, so the eye reads it as a directional event,
          // not a uniform burst.
          const waveDelay = ((screenX + (window.innerHeight - screenY)) / (window.innerWidth + window.innerHeight)) * 380;
          const jitter = Math.random() * 90;

          // Outward + upward velocity. Particles farther from center get
          // higher outward speed; everyone gets an upward bias so the dust
          // rises rather than collapsing.
          const angle = Math.atan2(dy, dx);
          const outwardSpeed = 80 + distNorm * 240 + Math.random() * 120;
          const vx = Math.cos(angle) * outwardSpeed + (Math.random() - 0.5) * 80;
          const vy = Math.sin(angle) * outwardSpeed * 0.6 - 200 - Math.random() * 220;

          particles.push({
            ox: screenX,
            oy: screenY,
            r,
            g,
            b,
            vx,
            vy,
            delay: waveDelay + jitter,
            life: 650 + Math.random() * 280,
            size: 1.8 + Math.random() * 1.2,
          });
        }
      }

      const canvas = canvasRef.current;
      if (!canvas) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.scale(dpr, dpr);

      setPhase('animating');
      const startTime = performance.now();

      const animate = () => {
        if (!mounted) return;
        const elapsed = performance.now() - startTime;
        ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

        for (const p of particles) {
          const age = elapsed - p.delay;
          if (age < 0) {
            // Pre-dissolution — render at original position, full opacity.
            ctx.globalAlpha = 1;
            ctx.fillStyle = `rgb(${p.r},${p.g},${p.b})`;
            ctx.fillRect(p.ox, p.oy, p.size, p.size);
            continue;
          }
          if (age >= p.life) continue;

          const t = age / p.life;
          const ease = 1 - Math.pow(1 - t, 3);
          const ageSec = age / 1000;
          const x = p.ox + p.vx * ease * 0.85;
          // Quadratic gravity term so particles arc realistically rather
          // than flying in a straight line.
          const y = p.oy + p.vy * ease * 0.85 + 0.5 * 380 * ageSec * ageSec;
          const opacity = Math.pow(1 - t, 1.4);
          const size = p.size * (1 - t * 0.35);

          ctx.globalAlpha = opacity;
          ctx.fillStyle = `rgb(${p.r},${p.g},${p.b})`;
          ctx.fillRect(x - size / 2, y - size / 2, size, size);
        }
        ctx.globalAlpha = 1;

        if (elapsed < TOTAL_MS + 100) {
          rafId = requestAnimationFrame(animate);
        }
      };
      animate();
    })();

    return () => {
      mounted = false;
      if (rafId !== null) cancelAnimationFrame(rafId);
      clearTimeout(completionTimer);
    };
  }, [onComplete]);

  return (
    <motion.div
      className="checkout-transition-root fixed inset-0 z-[100] pointer-events-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
    >
      {/* Black backdrop fades in once capture is done so the live UI behind
          us stops bleeding through gaps as particles diverge. */}
      <motion.div
        className="absolute inset-0 bg-black"
        initial={{ opacity: 0 }}
        animate={{ opacity: phase === 'animating' ? 1 : 0 }}
        transition={{ duration: 0.15 }}
      />

      {/* The actual disintegration — every pixel here was sampled from the
          UI you're leaving. */}
      <canvas ref={canvasRef} className="absolute inset-0" />

      {/* Tiny final flash to bridge into the checkout fade-in. */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(255,251,254,0.08), transparent 55%)',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0, 0.7, 0] }}
        transition={{ duration: TOTAL_MS / 1000, times: [0, 0.65, 0.85, 1], ease: 'easeOut' }}
      />
    </motion.div>
  );
}
